import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { MoreVertical, Trash2, Eye, Filter } from 'lucide-react';

const statuses = [
  { value: 'nouveau', label: 'Nouveau', color: 'bg-blue-100 text-blue-800' },
  { value: 'en_cours', label: 'En cours', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'confirme', label: 'Confirmé', color: 'bg-green-100 text-green-800' },
  { value: 'refuse', label: 'Refusé', color: 'bg-red-100 text-red-800' },
];

export default function ReservationManagement() {
  const [reservations, setReservations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await adminApi.getReservations();
      setReservations(r.data);
    } catch { toast.error('Erreur de chargement'); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.updateReservation(id, { status });
      load();
      toast.success('Statut mis à jour');
    } catch { toast.error('Erreur'); }
  };

  const deleteRes = async (id) => {
    if (!window.confirm('Supprimer cette réservation ?')) return;
    try {
      await adminApi.deleteReservation(id);
      load();
      toast.success('Supprimé');
    } catch { toast.error('Erreur'); }
  };

  const filtered = filter === 'all' ? reservations : reservations.filter(r => r.status === filter);
  const getStatusInfo = (s) => statuses.find(st => st.value === s) || statuses[0];

  const handleExport = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/export/reservations`, '_blank');
  };

  return (
    <AdminLayout title="Réservations">
      <div data-testid="admin-reservation-management">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Filter size={16} className="text-charcoal/50" />
            <div className="flex gap-2 overflow-x-auto">
              {[{ value: 'all', label: 'Toutes' }, ...statuses].map(s => (
                <button
                  key={s.value}
                  onClick={() => setFilter(s.value)}
                  data-testid={`admin-filter-${s.value}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    filter === s.value ? 'bg-garden-800 text-white' : 'bg-white text-charcoal border border-garden-200 hover:bg-garden-50'
                  }`}
                >
                  {s.label}
                  {s.value !== 'all' && (
                    <span className="ml-1">({reservations.filter(r => r.status === s.value).length})</span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-garden-800 hover:bg-garden-900 text-white rounded-lg text-xs font-semibold self-start sm:self-auto transition-all flex items-center gap-1.5 shadow-sm"
          >
            Exporter au format CSV
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-garden-200 overflow-hidden">
          {filtered.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-garden-50">
                <tr>
                  <th className="text-left p-3 font-medium text-garden-800">Nom</th>
                  <th className="text-left p-3 font-medium text-garden-800 hidden sm:table-cell">Date</th>
                  <th className="text-left p-3 font-medium text-garden-800 hidden md:table-cell">Pers.</th>
                  <th className="text-left p-3 font-medium text-garden-800 hidden lg:table-cell">Contact</th>
                  <th className="text-left p-3 font-medium text-garden-800">Statut</th>
                  <th className="text-right p-3 font-medium text-garden-800">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((res) => {
                  const status = getStatusInfo(res.status);
                  return (
                    <tr key={res.id} className="border-t border-garden-100 hover:bg-garden-50/50" data-testid={`admin-reservation-row-${res.id}`}>
                      <td className="p-3">
                        <span className="font-medium text-charcoal">{res.name}</span>
                        {res.message && <p className="text-xs text-charcoal/50 mt-0.5 truncate max-w-[200px]">{res.message}</p>}
                      </td>
                      <td className="p-3 text-charcoal/70 hidden sm:table-cell">{res.date} à {res.time}</td>
                      <td className="p-3 text-charcoal/70 hidden md:table-cell">{res.guests}</td>
                      <td className="p-3 hidden lg:table-cell">
                        <span className="text-xs text-charcoal/60">{res.email}</span>
                        <br />
                        <span className="text-xs text-charcoal/60">{res.phone}</span>
                      </td>
                      <td className="p-3">
                        <Select value={res.status} onValueChange={(v) => updateStatus(res.id, v)}>
                          <SelectTrigger className="w-32 h-8 text-xs border-0 p-0" data-testid={`admin-reservation-status-${res.id}`}>
                            <Badge className={`${status.color} border-0 text-xs`}>{status.label}</Badge>
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {statuses.map(s => (
                              <SelectItem key={s.value} value={s.value}>
                                <Badge className={`${s.color} border-0 text-xs`}>{s.label}</Badge>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger className="p-1 hover:bg-garden-100 rounded" data-testid={`admin-reservation-actions-${res.id}`}>
                            <MoreVertical size={16} className="text-charcoal/50" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-white">
                            <DropdownMenuItem onClick={() => setSelected(res)} className="text-xs">
                              <Eye size={12} className="mr-2" /> Voir détails
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteRes(res.id)} className="text-xs text-red-500">
                              <Trash2 size={12} className="mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-center py-12 text-charcoal/50 text-sm">Aucune réservation {filter !== 'all' ? 'avec ce statut' : ''}</p>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelected(null)}>
            <div className="bg-white w-full max-w-md p-6 overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="reservation-detail-panel">
              <h2 className="font-heading font-semibold text-xl text-garden-800 mb-4">Détails de la réservation</h2>
              <div className="space-y-3 text-sm">
                <div><span className="text-charcoal/50">Nom:</span> <span className="font-medium">{selected.name}</span></div>
                <div><span className="text-charcoal/50">Email:</span> <span>{selected.email}</span></div>
                <div><span className="text-charcoal/50">Téléphone:</span> <a href={`tel:${selected.phone}`} className="text-garden-600">{selected.phone}</a></div>
                <div><span className="text-charcoal/50">Date:</span> <span>{selected.date} à {selected.time}</span></div>
                <div><span className="text-charcoal/50">Personnes:</span> <span>{selected.guests}</span></div>
                <div><span className="text-charcoal/50">Message:</span> <span>{selected.message || '-'}</span></div>
                <div><span className="text-charcoal/50">Statut:</span> <Badge className={`${getStatusInfo(selected.status).color} border-0`}>{getStatusInfo(selected.status).label}</Badge></div>
                <div><span className="text-charcoal/50">Reçue le:</span> <span>{selected.created_at}</span></div>
              </div>
              <button onClick={() => setSelected(null)} className="mt-6 text-sm text-garden-600 hover:text-garden-800">Fermer</button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
